import type { NowPlaying, Radio } from '../../audio/radio/radio';
import { el, setText } from '../components/el';
import { RadioList } from './radioList';

const AUTO_COLLAPSE_MS = 7000;

/**
 * Dashboard radio: |◀ [MIX Palm Wine FM · Built-in · Highlife] ▶| ❚❚ AUX. Tap the screen for the station list.
 * On phones while driving it shrinks to a ♪ chip and a play/pause button; ♪ opens the full bar for a few seconds.
 */
export function createRadioBar(parent: HTMLElement, radio: Radio): HTMLElement {
  const root = el('div', 'radio-bar');
  const toggle = el('button', 'radio-btn radio-toggle', '♪');
  const prev = el('button', 'radio-btn', '|◀');
  const next = el('button', 'radio-btn', '▶|');
  const play = el('button', 'radio-btn play', '❚❚');
  const aux = el('button', 'radio-btn aux', 'AUX');
  const screen = el('button', 'radio-screen');
  const badge = el('b', 'radio-badge');
  const name = el('span', 'radio-name');
  const detail = el('small', 'radio-detail');
  const top = el('div', 'radio-top');
  const list = new RadioList(radio, () => list.close());
  prev.title = 'Previous station (Shift+R)';
  next.title = 'Next station (R)';
  screen.title = 'Pick a station';
  top.append(badge, name, el('span', 'radio-caret', '▾'));
  screen.append(top, detail);
  root.append(toggle, prev, screen, next, play, aux, list.root);
  const show = (now: NowPlaying): void => {
    setText(badge, now.badge);
    setText(name, now.name);
    setText(detail, now.paused ? 'Paused · tap ▶ to play' : now.detail);
    const stopped = now.paused || now.badge === 'OFF';
    setText(play, stopped ? '▶' : '❚❚');
    play.title = stopped ? 'Play (T)' : 'Pause (T)';
    root.dataset.badge = now.badge;
    root.dataset.paused = String(now.paused);
  };
  radio.onChange = show;
  show(radio.nowPlaying);
  prev.addEventListener('click', () => radio.prev());
  next.addEventListener('click', () => radio.next());
  play.addEventListener('click', () => radio.togglePause());
  aux.addEventListener('click', () => void radio.toggleAux());
  screen.addEventListener('click', () => void list.toggle());

  // Compact mode (phones, driving): ♪ expands the bar, which folds away again after a few seconds.
  let collapseTimer = 0;
  const setExpanded = (expanded: boolean): void => {
    root.classList.toggle('expanded', expanded);
    setText(toggle, expanded ? '✕' : '♪');
    window.clearTimeout(collapseTimer);
    if (expanded) collapseTimer = window.setTimeout(() => setExpanded(false), AUTO_COLLAPSE_MS);
    else list.close();
  };
  toggle.addEventListener('click', () => setExpanded(!root.classList.contains('expanded')));
  root.addEventListener('click', () => {
    if (root.classList.contains('expanded')) setExpanded(true);
  });
  // Buttons keep focus after a click; blur so Space (hop) doesn't press them again.
  root.addEventListener('click', () => (document.activeElement as HTMLElement | null)?.blur());
  document.addEventListener('pointerdown', (e) => {
    if (list.isOpen && !root.contains(e.target as Node)) list.close();
    if (root.classList.contains('expanded') && !root.contains(e.target as Node)) setExpanded(false);
  });
  parent.append(root);
  return root;
}
